import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
/**
 * MercadoPagoCheckoutForm — Checkout embebido con MP Payment Brick
 *
 * Flujo completo (embedded, sin redirect a MP):
 *   1. Parent crea preference via /api/mercadopago/create-preference → preferenceId
 *   2. Este componente muestra el Payment Brick de @mercadopago/sdk-react
 *   3. Usuario llena datos de tarjeta → brick genera token y llama onSubmit
 *   4. onSubmit llama a /api/mercadopago/process-payment con el token
 *   5. Si status "approved" → onSuccess(paymentId)
 *      Si "pending" → onSuccess(paymentId) con flag pending
 *      Si "rejected" → muestra error en el brick
 *
 * Requiere VITE_MERCADOPAGO_PUBLIC_KEY en el frontend.
 */

import { initMercadoPago, Payment } from "@mercadopago/sdk-react";
import type { IPaymentFormData } from "@mercadopago/sdk-react/esm/bricks/payment/type";
import type { IBrickError } from "@mercadopago/sdk-react/esm/bricks/util/types/common";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackPurchaseMeta } from "@/lib/metaPixelAnalytics";
import { getCheckoutAttributionMetadataPostHog } from "@/lib/posthogAnalytics";

// ─── MP singleton ─────────────────────────────────────────────────────────────
const MP_PUBLIC_KEY = import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY as string | undefined;

let _mpInitialized = false;
function ensureMpInitialized() {
  if (!_mpInitialized && MP_PUBLIC_KEY) {
    initMercadoPago(MP_PUBLIC_KEY, { locale: "es-AR" });
    _mpInitialized = true;
  }
}

// ─── Tipos ────────────────────────────────────────────────────────────────────
interface MercadoPagoCheckoutFormProps {
  /** Precio en ARS (unidades mayores, no centavos) */
  amount: number;
  /** preferenceId devuelto por /api/mercadopago/create-preference */
  preferenceId: string;
  /** Nombre del producto para el header */
  packName: string;
  /** Metadata adicional para el proceso de pago */
  metadata?: Record<string, unknown>;
  /** external_reference para reconciliación */
  externalReference?: string;
  /** Tipo de pago: "subscription" (mensual) o "one-time" (pago único). Default: "subscription" */
  paymentType?: "subscription" | "one-time";
  /** Callback al pago exitoso/pending */
  onSuccess: (paymentId: number, status: string) => void;
  /** Callback al cerrar/volver */
  onBack: () => void;
  /** Callback si hay error de inicialización */
  onError?: (err: string) => void;
  /** Email del pagador capturado en el brick (backup recovery si difiere del wizard) */
  onPayerEmailCapture?: (email: string) => void;
}

export function MercadoPagoCheckoutForm({
  amount,
  preferenceId,
  packName,
  metadata,
  externalReference,
  paymentType = "subscription",
  onSuccess,
  onBack,
  onError,
  onPayerEmailCapture,
}: MercadoPagoCheckoutFormProps) {
  const { t } = useTranslation();
  const [brickReady, setBrickReady] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const initCalled = useRef(false);

  useEffect(() => {
    if (initCalled.current) return;
    initCalled.current = true;

    if (!MP_PUBLIC_KEY) {
      const msg = t("mercadoPagoForm.initErrorKey");
      setInitError(msg);
      onError?.(msg);
      return;
    }
    ensureMpInitialized();
    // Pequeño delay para que el brick monte correctamente
    const t = setTimeout(() => setBrickReady(true), 200);
    return () => clearTimeout(t);
  }, [onError, t]);

  const formatARS = (v: number) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
    }).format(v);

  // ── Procesar pago en backend tras recibir token del brick ──────────────────
  const handleSubmit = async (formData: IPaymentFormData): Promise<void> => {
    setIsProcessing(true);
    setPaymentError(null);

    try {
      const cardData = formData.formData;
      const brickEmail = cardData.payer?.email?.trim();
      if (brickEmail && brickEmail.includes("@")) {
        onPayerEmailCapture?.(brickEmail);
      }

      const response = await fetch("/api/mercadopago/process-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: cardData.token,
          payment_method_id: cardData.payment_method_id,
          issuer_id: cardData.issuer_id,
          installments: cardData.installments,
          transaction_amount: cardData.transaction_amount || amount,
          payer: cardData.payer,
          description: packName,
          external_reference: externalReference,
          metadata: {
            source: BRAND_SOURCE_KEY,
            preference_id: preferenceId,
            // Página de origen + referrer/UTM → Slack sale_confirmed del webhook MP
            ...getCheckoutAttributionMetadataPostHog(),
            ...metadata,
          },
        }),
      });

      const data = await response.json() as {
        id?: number;
        status?: string;
        status_detail?: string;
        error?: string;
      };

      if (!response.ok || !data.id) {
        throw new Error(data.error || t("mercadoPagoForm.processError"));
      }

      if (data.status === "rejected") {
        if (brickEmail && brickEmail.includes("@")) {
          onPayerEmailCapture?.(brickEmail);
        }
        const detail = data.status_detail || "rejected";
        const msg = getRejectedMessage(t, detail);
        setPaymentError(msg);
        setIsProcessing(false);
        return;
      }

      // approved o pending → Meta Pixel Purchase event
      if (data.status === "approved" || data.status === "pending") {
        trackPurchaseMeta({
          value: amount,
          currency: "ARS",
          content_ids: [externalReference ?? String(data.id)],
          content_name: packName,
          content_type: paymentType === "subscription" ? "product" : "product",
          num_items: 1,
        });
      }
      onSuccess(data.id, data.status ?? "approved");
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("mercadoPagoForm.connectionError");
      setPaymentError(msg);
      setIsProcessing(false);
    }
  };

  const handleBrickError = (error: IBrickError): void => {
    // errores non_critical son informativos (e.g. validación de campos), no bloqueantes
    if (error.type === "non_critical") return;
    console.error("[MPBrick] critical error:", error);
    setPaymentError(error.message || t("mercadoPagoForm.brickError"));
  };

  // ── Error de inicialización ───────────────────────────────────────────────
  if (initError) {
    return (
      <div className="w-full h-full flex flex-col bg-black text-white items-center justify-center p-6 gap-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 max-w-sm w-full text-center">
          <p className="text-red-400 text-sm">{initError}</p>
        </div>
        <button type="button" onClick={onBack} className="text-white/60 text-sm underline">
          {t("mercadoPagoForm.back")}
        </button>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full flex flex-col bg-[#0A0E27] text-white font-body"
    >
      {/* ── Header iOS style ── */}
      <div
        className="flex-shrink-0 bg-[#0A0E27] border-b border-white/[0.08]"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="flex items-center justify-between px-4 min-h-[56px] h-14">
          <button
            type="button"
            onClick={onBack}
            disabled={isProcessing}
            aria-label={t("mercadoPagoForm.backAria")}
            className="flex items-center gap-1 min-h-[44px] min-w-[44px] -ml-2 pl-2 text-[#009EE3] active:opacity-60 transition-opacity disabled:opacity-30 touch-manipulation"
          >
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-[16px] font-semibold">{t("mercadoPagoForm.back")}</span>
          </button>
          <h2 className="text-[18px] font-bold text-white">
            {t("mercadoPagoForm.checkoutTitle")}
          </h2>
          <div className="w-20 flex-shrink-0" />
        </div>
      </div>

      {/* ── Mercado Pago branding ── */}
      <div className="flex-shrink-0 bg-gradient-to-b from-[#009EE3] to-[#0077B5] px-4 py-3.5">
        <div className="flex items-center justify-center gap-2">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-white text-[13px] font-bold">{t("mercadoPagoForm.secureBanner")}</span>
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto bg-[#0A0E27]">
        <div className="px-4 py-5 space-y-4 max-w-lg mx-auto pb-8">

          {/* Product summary — iOS card style */}
          <div className="bg-[#1C1F3A] rounded-2xl p-4 border border-white/[0.06]">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-white text-[16px] font-bold leading-snug">
                  {packName}
                </h3>
                {paymentType === "subscription" && (
                  <p className="text-white/50 text-[14px] mt-1 leading-tight font-medium">{t("mercadoPagoForm.monthly")}</p>
                )}
                {paymentType === "one-time" && (
                  <p className="text-white/50 text-[14px] mt-1 leading-tight font-medium">{t("mercadoPagoForm.oneTime")}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-white text-[20px] font-bold">
                  {formatARS(amount)}
                </div>
                {paymentType === "subscription" && (
                  <div className="text-white/40 text-[12px] mt-0.5 font-medium">{t("mercadoPagoForm.perMonth")}</div>
                )}
              </div>
            </div>
          </div>

          {/* Processing overlay */}
          {isProcessing && (
            <div className="flex items-center justify-center gap-3 py-5 bg-[#009EE3]/10 border border-[#009EE3]/30 rounded-2xl">
              <Loader2 className="w-5 h-5 animate-spin text-[#009EE3]" />
              <span className="text-white text-[16px] font-semibold">{t("mercadoPagoForm.processing")}</span>
            </div>
          )}

          {/* Payment error */}
          {paymentError && (
            <div role="alert" className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-red-200 text-[14px] font-semibold flex-1 leading-snug">{paymentError}</p>
              </div>
            </div>
          )}

          {/* ── MP Payment Brick ── */}
          <div className="space-y-3">
            <label className="block text-white text-[16px] font-bold px-1">
              {t("mercadoPagoForm.paymentData")}
            </label>

            {!brickReady ? (
              <div className="flex items-center justify-center py-20 bg-[#1C1F3A] rounded-2xl">
                <Loader2 className="w-8 h-8 animate-spin text-white/20" />
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden border border-white/[0.06]">
                <Payment
                  initialization={{
                    amount,
                    preferenceId,
                  }}
                  customization={{
                    paymentMethods: {
                      creditCard: "all",
                      debitCard: "all",
                      mercadoPago: "all", // Dinero en cuenta de MercadoPago
                    },
                    visual: {
                      style: {
                        theme: "dark",
                        customVariables: {
                          baseColor: "#009EE3",
                          textPrimaryColor: "#ffffff",
                          textSecondaryColor: "rgba(255, 255, 255, 0.6)",
                          inputBackgroundColor: "#1C1F3A",
                          formBackgroundColor: "#0A0E27",
                          borderRadiusSmall: "12px",
                          borderRadiusMedium: "16px",
                          borderRadiusLarge: "20px",
                          fontSizeSmall: "14px",
                          fontSizeMedium: "16px",
                          fontSizeLarge: "18px",
                          fontWeightNormal: "500",
                          fontWeightSemiBold: "700",
                          buttonTextColor: "#ffffff",
                          warningColor: "#FF9500",
                          errorColor: "#FF3B30",
                          successColor: "#34C759",
                        },
                      },
                    },
                  }}
                  onSubmit={handleSubmit}
                  onError={handleBrickError}
                  onReady={() => {
                    // Brick listo
                  }}
                />
              </div>
            )}
          </div>

          {/* Security footer */}
          <div className="flex items-center justify-center gap-2 text-white/30 text-[12px] pt-3 pb-2">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-semibold">{t("mercadoPagoForm.footerSecure")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

type TFn = (key: string) => string;

function getRejectedMessage(t: TFn, statusDetail: string): string {
  const keys: Record<string, string> = {
    cc_rejected_insufficient_amount: "mercadoPagoForm.rejected_insufficient",
    cc_rejected_bad_filled_card_number: "mercadoPagoForm.rejected_card_number",
    cc_rejected_bad_filled_date: "mercadoPagoForm.rejected_date",
    cc_rejected_bad_filled_security_code: "mercadoPagoForm.rejected_cvv",
    cc_rejected_bad_filled_other: "mercadoPagoForm.rejected_other",
    cc_rejected_blacklist: "mercadoPagoForm.rejected_blacklist",
    cc_rejected_call_for_authorize: "mercadoPagoForm.rejected_authorize",
    cc_rejected_card_disabled: "mercadoPagoForm.rejected_disabled",
    cc_rejected_duplicated_payment: "mercadoPagoForm.rejected_duplicate",
    cc_rejected_high_risk: "mercadoPagoForm.rejected_risk",
    cc_rejected_max_attempts: "mercadoPagoForm.rejected_attempts",
  };
  const k = keys[statusDetail];
  return k ? t(k) : t("mercadoPagoForm.rejectedDefault");
}
